/**
 * Community Facilities Service
 * Real-time community-submitted facility polling and caching
 */

export interface CommunityFacility {
  id: string;
  telegram_user_id: string;
  facility_type: string;
  name: string;
  api_number?: string;
  operator?: string;
  latitude: number;
  longitude: number;
  county?: string;
  notes?: string;
  photo_url?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

type FacilityListener = (facilities: CommunityFacility[], newFacilities: CommunityFacility[]) => void;

class CommunityFacilitiesService {
  private facilities: CommunityFacility[] = [];
  private listeners: Set<FacilityListener> = new Set();
  private pollInterval: number | null = null;
  private apiUrl = '/api/community-facilities';

  // Start polling for new facilities
  startPolling(intervalMs: number = 30000) {
    if (this.pollInterval) return;
    
    // Fetch immediately
    this.fetchFacilities();
    
    // Then poll at interval
    this.pollInterval = window.setInterval(() => {
      this.fetchFacilities();
    }, intervalMs);
    
    console.log('[CommunityFacilities] Started polling every', intervalMs / 1000, 'seconds');
  }

  // Stop polling
  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
      console.log('[CommunityFacilities] Stopped polling');
    }
  }

  // Subscribe to facility updates
  subscribe(listener: FacilityListener): () => void {
    this.listeners.add(listener);
    
    // Immediately notify with current facilities
    if (this.facilities.length > 0) {
      listener(this.facilities, []);
    }
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  // Get current facilities
  getFacilities(): CommunityFacility[] {
    return [...this.facilities];
  }

  // Fetch facilities from API
  async fetchFacilities(): Promise<CommunityFacility[]> {
    try {
      const response = await fetch(`${this.apiUrl}?limit=1000&status=all`);
      const data = await response.json();
      
      if (data.success && Array.isArray(data.facilities)) {
        const oldIds = new Set(this.facilities.map(f => f.id));
        const newFacilities = data.facilities.filter((f: CommunityFacility) => !oldIds.has(f.id));
        
        this.facilities = data.facilities;
        
        // Notify listeners
        if (newFacilities.length > 0 || this.facilities.length !== oldIds.size) {
          this.notifyListeners(newFacilities);
        }
        
        return this.facilities;
      }
    } catch (error) {
      console.error('[CommunityFacilities] Fetch error:', error);
    }
    
    return this.facilities;
  }

  // Add a new facility optimistically (before server confirms)
  addOptimistic(facility: CommunityFacility): void {
    this.facilities = [facility, ...this.facilities];
    this.notifyListeners([facility]);
  }

  // Notify all listeners
  private notifyListeners(newFacilities: CommunityFacility[]) {
    this.listeners.forEach(listener => {
      try {
        listener(this.facilities, newFacilities);
      } catch (error) {
        console.error('[CommunityFacilities] Listener error:', error);
      }
    });
  }
}

// Singleton instance
export const communityFacilitiesService = new CommunityFacilitiesService();
